/**
 * Build-time environment variables consumed by the dashboard SPA.
 *
 * `VITE_API_ORIGIN` — the Sill API origin (`services/api`). Used by:
 *
 *   - The HTTP client (`api/http-client.ts`) for every cross-origin
 *     `fetch` to `/v1/*` (the dashboard at `app.sill.so` posts cross-
 *     origin to `api.sill.so` per ADR-0015 §1 / ADR-0017).
 *   - The `<Verify />` view (`views/login/Verify.tsx`) for the
 *     top-level navigation to `/v1/auth/verify?token=...` per
 *     ADR-0018 §6.2.
 *
 * Local-dev default: `http://localhost:8080` per
 * `services/api/src/config/env.ts:224` (the API listens on 8080; the
 * edge worker is a separate process on 8787 and is NOT in the
 * dashboard's call path).
 *
 * **Production guard.** Vite inlines `VITE_*` vars at build time. If a
 * production build runs without `VITE_API_ORIGIN` set, the previous
 * shape silently fell back to `http://localhost:8080`, the bundle
 * shipped with that literal baked in, and merchants hit
 * `ERR_CONNECTION_REFUSED` on every sign-in attempt. The guard below
 * fails the build instead of shipping a broken bundle: any
 * production build (`import.meta.env.PROD === true`) that doesn't
 * provide a usable API origin throws at module load, which surfaces
 * as a Vite build failure. Dev (`import.meta.env.DEV`) keeps the
 * localhost fallback so `pnpm dev` works out of the box.
 *
 * "Usable" = non-empty, and not a localhost-shaped URL — production
 * pointing at localhost is the exact failure mode the guard exists
 * to prevent.
 */

const raw = (import.meta.env['VITE_API_ORIGIN'] as string | undefined)?.trim();

function isUsableProductionOrigin(value: string | undefined): boolean {
  if (value === undefined || value.length === 0) return false;
  // Localhost-shaped URLs are dev-only. A production bundle pointing
  // at `localhost`, `127.0.0.1`, or `0.0.0.0` is a misconfiguration —
  // every merchant browser would try to reach their own machine.
  if (/^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:|\/|$)/i.test(value)) {
    return false;
  }
  return true;
}

if (import.meta.env.PROD === true && !isUsableProductionOrigin(raw)) {
  throw new Error(
    'VITE_API_ORIGIN must be set to a non-localhost URL for production builds. '
      + 'Example: `VITE_API_ORIGIN=https://api.sill.so pnpm --filter @sill/dashboard run build`. '
      + `Got: ${raw === undefined ? '(unset)' : JSON.stringify(raw)}.`,
  );
}

export const API_ORIGIN: string = raw ?? 'http://localhost:8080';

/**
 * ADR-0185 §2.3 — Cloudflare Turnstile site key (PUBLIC by design; the
 * secret half lives only in the API's env). Consumed by the /welcome
 * confirm page when the signup breaker has challenged the request.
 * OPTIONAL: null means Turnstile is not provisioned — the page then
 * shows the paused copy instead of a widget (the API fails closed on
 * its side too, so the two states can never disagree for long).
 */
const raw_turnstile = (import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined)?.trim();
export const TURNSTILE_SITE_KEY: string | null =
  raw_turnstile !== undefined && raw_turnstile.length > 0 ? raw_turnstile : null;
